import { useCallback, useEffect, useState } from 'react';
import {
  listReseaux,
  createReseau,
  updateReseau,
  deleteReseau,
  cibleLabel,
  fetchReseauMemberCounts,
  RESEAU_CIBLES,
  type Reseau,
  type ReseauInput,
  type ReseauCible,
} from '@/lib/networks';

// ─────────────────────────────────────────────────────────────
// SuperAdmin — Gestion des réseaux internes (ex. « Zifek Professionnel »).
// Créer / modifier un réseau : titre, description, règles, icône,
// cible (« chez qui cela doit s'afficher ») et activation.
// ─────────────────────────────────────────────────────────────

const ICON_SUGGESTIONS = [
  'ri-share-forward-line',
  'ri-global-line',
  'ri-p2p-line',
  'ri-links-line',
  'ri-store-3-line',
  'ri-community-line',
  'ri-vip-diamond-line',
  'ri-award-line',
];

const EMPTY_FORM: ReseauInput = {
  titre: '',
  description: '',
  regles: '',
  icon: 'ri-share-forward-line',
  cible: 'both',
  actif: true,
  sort_order: 0,
};

export default function SuperAdminReseauxPage() {
  const [reseaux, setReseaux] = useState<Reseau[]>([]);
  const [counts, setCounts] = useState<Map<number, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Reseau | null>(null);
  const [form, setForm] = useState<ReseauInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, memberCounts] = await Promise.all([listReseaux(), fetchReseauMemberCounts()]);
      setReseaux(list);
      setCounts(memberCounts);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger les réseaux.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM, sort_order: reseaux.length });
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (r: Reseau) => {
    setEditing(r);
    setForm({
      titre: r.titre,
      description: r.description,
      regles: r.regles,
      icon: r.icon,
      cible: r.cible,
      actif: r.actif,
      sort_order: r.sort_order,
    });
    setFormError('');
    setFormOpen(true);
  };

  const save = async () => {
    if (!form.titre.trim()) {
      setFormError('Le titre du réseau est obligatoire.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      if (editing) await updateReseau(editing.id, form);
      else await createReseau(form);
      setFormOpen(false);
      await load();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Échec de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActif = async (r: Reseau) => {
    setBusyId(r.id);
    setReseaux((prev) => prev.map((x) => (x.id === r.id ? { ...x, actif: !x.actif } : x)));
    try {
      await updateReseau(r.id, { actif: !r.actif });
    } catch {
      setReseaux((prev) => prev.map((x) => (x.id === r.id ? { ...x, actif: r.actif } : x)));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (r: Reseau) => {
    setBusyId(r.id);
    try {
      await deleteReseau(r.id);
      setReseaux((prev) => prev.filter((x) => x.id !== r.id));
    } catch {
      setError('Impossible de supprimer ce réseau.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Réseaux</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Créez des réseaux internes (ex. « Zifek Professionnel ») et définissez chez qui ils s&apos;affichent.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
        >
          <i className="ri-add-line"></i>
          Nouveau réseau
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : reseaux.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-share-forward-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-600">Aucun réseau. Créez le premier.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {reseaux.map((r) => (
            <div key={r.id} className={`bg-background-50 border rounded-lg p-5 ${r.actif ? 'border-background-200/70' : 'border-background-200/70 opacity-60'}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${r.actif ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-500'}`}>
                  <i className={`${r.icon || 'ri-share-forward-line'} text-xl`}></i>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-bold text-foreground-950">{r.titre}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${r.actif ? 'bg-accent-100 text-accent-900' : 'bg-foreground-100 text-foreground-600'}`}>
                      {r.actif ? 'Actif' : 'Inactif'}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap bg-secondary-100 text-secondary-900">
                      <i className="ri-group-line"></i>
                      {counts.get(r.id) || 0} membre{(counts.get(r.id) || 0) > 1 ? 's' : ''}
                    </span>
                  </div>
                  <p className="text-[11px] text-foreground-400 mt-0.5 flex items-center gap-1">
                    <i className="ri-focus-3-line"></i>
                    {cibleLabel(r.cible)}
                  </p>
                </div>
              </div>

              {r.description && <p className="text-xs text-foreground-600 leading-relaxed mb-3 line-clamp-3">{r.description}</p>}

              {r.regles && (
                <pre className="whitespace-pre-wrap break-words rounded-lg bg-background-100 px-3 py-2.5 text-[11px] leading-relaxed text-foreground-600 font-sans mb-3 line-clamp-4">
                  {r.regles}
                </pre>
              )}

              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEdit(r)}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors"
                >
                  <i className="ri-pencil-line"></i>
                  Modifier
                </button>
                <button
                  onClick={() => toggleActif(r)}
                  disabled={busyId === r.id}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors disabled:opacity-50 ${r.actif ? 'bg-accent-100 text-accent-900 hover:bg-accent-200' : 'bg-background-100 text-foreground-600 hover:bg-background-200/70'}`}
                >
                  {busyId === r.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className={r.actif ? 'ri-eye-off-line' : 'ri-eye-line'}></i>}
                  {r.actif ? 'Désactiver' : 'Activer'}
                </button>
                <button
                  onClick={() => remove(r)}
                  disabled={busyId === r.id}
                  title="Supprimer le réseau"
                  className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer disabled:opacity-50"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" onClick={() => setFormOpen(false)}>
          <div className="bg-background-50 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200/70">
              <h3 className="text-base font-semibold text-foreground-950">{editing ? 'Modifier le réseau' : 'Nouveau réseau'}</h3>
              <button onClick={() => setFormOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Titre</label>
                <input
                  type="text"
                  value={form.titre}
                  onChange={(e) => setForm({ ...form, titre: e.target.value })}
                  placeholder="ex. Zifek Professionnel"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Règles</label>
                <textarea
                  value={form.regles}
                  onChange={(e) => setForm({ ...form, regles: e.target.value })}
                  rows={4}
                  placeholder={'1. Condition...\n2. Condition...'}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">S&apos;affiche chez</label>
                <select
                  value={form.cible}
                  onChange={(e) => setForm({ ...form, cible: e.target.value as ReseauCible })}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 cursor-pointer"
                >
                  {RESEAU_CIBLES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1.5">Icône</label>
                <div className="flex items-center gap-2">
                  <span className="w-10 h-10 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                    <i className={`${form.icon || 'ri-share-forward-line'} text-lg text-foreground-600`}></i>
                  </span>
                  <input
                    type="text"
                    value={form.icon}
                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                    className="flex-1 px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                  />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {ICON_SUGGESTIONS.map((ic) => (
                    <button
                      key={ic}
                      type="button"
                      onClick={() => setForm({ ...form, icon: ic })}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${form.icon === ic ? 'bg-primary-100 text-primary-700' : 'bg-background-100 text-foreground-500 hover:bg-background-200/70'}`}
                    >
                      <i className={ic}></i>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-500 mb-1.5">Ordre</label>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, actif: form.actif === false })}
                    className="w-full flex items-center justify-between p-3 bg-background-100 rounded-lg cursor-pointer"
                  >
                    <span className="text-xs font-medium text-foreground-700">{form.actif !== false ? 'Actif' : 'Inactif'}</span>
                    <span className={`w-11 h-6 rounded-full flex items-center px-0.5 transition-colors ${form.actif !== false ? 'bg-accent-500 justify-end' : 'bg-background-300/70 justify-start'}`}>
                      <span className="w-5 h-5 rounded-full bg-background-50 block"></span>
                    </span>
                  </button>
                </div>
              </div>

              {formError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-center gap-1.5">
                  <i className="ri-error-warning-line"></i>
                  {formError}
                </p>
              )}
            </div>

            <div className="flex gap-3 px-5 py-4 border-t border-background-200/70">
              <button
                onClick={() => setFormOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 disabled:opacity-50 transition-colors"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                {editing ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}